import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { PageHeader } from '../../components/PageHeader';
import { morseToText, textToMorse } from './morseTable';
import { MorseVisualizer } from './MorseVisualizer';
import { PlaybackHandle, computeSchedule, playMorse } from './player';
import './Morse.css';

const INITIAL_TEXT = 'CIAO LO SAI';

function Morse() {
    const [text, setText] = useState(INITIAL_TEXT);
    const [morse, setMorse] = useState(textToMorse(INITIAL_TEXT));
    const [copied, setCopied] = useState<'text' | 'morse' | null>(null);
    const [playing, setPlaying] = useState(false);
    const [playingIndex, setPlayingIndex] = useState<number>(-1);
    const [wpm, setWpm] = useState(15);
    const [freq, setFreq] = useState(600);

    const ctxRef = useRef<AudioContext | null>(null);
    const handleRef = useRef<PlaybackHandle | null>(null);
    const indexTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

    const handleTextChange = (v: string) => {
        setText(v);
        setMorse(textToMorse(v));
    };

    const handleMorseChange = (v: string) => {
        setMorse(v);
        setText(morseToText(v));
    };

    const clearTimeouts = () => {
        indexTimeoutsRef.current.forEach((t) => clearTimeout(t));
        indexTimeoutsRef.current = [];
    };

    const stopPlayback = useCallback(() => {
        if (handleRef.current) {
            handleRef.current.stop();
            handleRef.current = null;
        }
        clearTimeouts();
        setPlaying(false);
        setPlayingIndex(-1);
    }, []);

    const play = useCallback(() => {
        stopPlayback();
        const source = morse;
        if (!source.trim()) return;

        let ctx = ctxRef.current;
        if (!ctx || ctx.state === 'closed') {
            ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
            ctxRef.current = ctx;
        }
        if (ctx.state === 'suspended') ctx.resume();

        const handle = playMorse(ctx, source, { wpm, frequency: freq });
        handleRef.current = handle;
        setPlaying(true);

        const visualSchedule = computeSchedule(source, wpm);
        visualSchedule.symbols.forEach((sym, i) => {
            const onT = setTimeout(() => setPlayingIndex(i), sym.startMs);
            const offT = setTimeout(() => {
                setPlayingIndex((curr) => (curr === i ? -1 : curr));
            }, sym.endMs);
            indexTimeoutsRef.current.push(onT, offT);
        });
        const endT = setTimeout(() => {
            handleRef.current = null;
            setPlaying(false);
            setPlayingIndex(-1);
        }, visualSchedule.totalMs + 200);
        indexTimeoutsRef.current.push(endT);
    }, [morse, wpm, freq, stopPlayback]);

    const copy = async (value: string, which: 'text' | 'morse') => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(which);
            setTimeout(() => setCopied(null), 1200);
        } catch {
            // ignore
        }
    };

    const clear = () => {
        stopPlayback();
        setText('');
        setMorse('');
    };

    useEffect(() => {
        return () => {
            clearTimeouts();
            if (handleRef.current) handleRef.current.stop();
            if (ctxRef.current && ctxRef.current.state !== 'closed') {
                ctxRef.current.close().catch(() => {});
            }
        };
    }, []);

    return (
        <div className="morse-page">
            <Helmet>
                <title>Morse · Lo.Sai</title>
                <meta name="robots" content="noindex" />
            </Helmet>
            <PageHeader title="MORSE" />

            <div className="morse-container">
                <p className="morse-lede">
                    Translate between text and Morse code. Type on either side and the other follows.
                    Hit play to hear it.
                </p>

                <section className="morse-strip">
                    <div className="morse-strip-head">
                        <div className="morse-strip-meta">
                            <span>Now sounding</span>
                            <span className="morse-strip-meta-sep">·</span>
                            <span>{wpm} WPM</span>
                            <span className="morse-strip-meta-sep">·</span>
                            <span>{freq} Hz</span>
                        </div>
                        <button
                            className={`morse-play ${playing ? 'is-on' : ''}`}
                            onClick={() => (playing ? stopPlayback() : play())}
                            disabled={!morse.trim()}
                        >
                            <span className="morse-play-glyph" aria-hidden="true">
                                {playing ? '■' : '▶'}
                            </span>
                            {playing ? 'Stop' : 'Play'}
                        </button>
                    </div>
                    <MorseVisualizer morse={morse} playingIndex={playingIndex} />
                </section>

                <section className="morse-board">
                    <div className="morse-pane">
                        <div className="morse-pane-head">
                            <span className="morse-pane-label">Text</span>
                            <button
                                className="morse-link"
                                onClick={() => copy(text, 'text')}
                                disabled={!text}
                            >
                                {copied === 'text' ? 'Copied' : 'Copy'}
                            </button>
                        </div>
                        <textarea
                            className="morse-input"
                            value={text}
                            onChange={(e) => handleTextChange(e.target.value)}
                            placeholder="Type any text…"
                            spellCheck={false}
                            rows={5}
                        />
                    </div>

                    <div className="morse-pane">
                        <div className="morse-pane-head">
                            <span className="morse-pane-label">Morse</span>
                            <button
                                className="morse-link"
                                onClick={() => copy(morse, 'morse')}
                                disabled={!morse}
                            >
                                {copied === 'morse' ? 'Copied' : 'Copy'}
                            </button>
                        </div>
                        <textarea
                            className="morse-input morse-input-mono"
                            value={morse}
                            onChange={(e) => handleMorseChange(e.target.value)}
                            placeholder=". - / . - .  …"
                            spellCheck={false}
                            rows={5}
                        />
                    </div>
                </section>

                <section className="morse-controls">
                    <label className="morse-field">
                        <span>Speed</span>
                        <div className="morse-field-input">
                            <input
                                type="number"
                                min={5}
                                max={40}
                                value={wpm}
                                onChange={(e) => setWpm(Number(e.target.value) || 15)}
                            />
                            <em>wpm</em>
                        </div>
                    </label>
                    <label className="morse-field">
                        <span>Pitch</span>
                        <div className="morse-field-input">
                            <input
                                type="number"
                                min={200}
                                max={2000}
                                step={50}
                                value={freq}
                                onChange={(e) => setFreq(Number(e.target.value) || 600)}
                            />
                            <em>Hz</em>
                        </div>
                    </label>
                    <button className="morse-link morse-clear" onClick={clear}>
                        Clear
                    </button>
                </section>

                <details className="morse-legend">
                    <summary>How it works</summary>
                    <ul>
                        <li>One space between letters (<code>. -</code> is A, <code>- . . .</code> is B).</li>
                        <li>A slash <code>/</code> separates words.</li>
                        <li>Unknown Morse symbols come back as <code>?</code>.</li>
                        <li>Supported: A–Z, 0–9 and common punctuation.</li>
                    </ul>
                </details>
            </div>
        </div>
    );
}

export default Morse;
